import { TrendPattern } from '../../lib/api/intelligence';

interface TrendingPatternsPanelProps {
  patterns: TrendPattern[];
  loading?: boolean;
}

function TrendArrow({ direction }: { direction: number }) {
  if (direction > 0) {
    return (
      <span className="text-red-600 dark:text-red-400 font-bold text-base" aria-label="trending up">
        ↑
      </span>
    );
  }
  if (direction < 0) {
    return (
      <span className="text-green-600 dark:text-green-400 font-bold text-base" aria-label="trending down">
        ↓
      </span>
    );
  }
  return (
    <span className="text-gray-400 font-bold text-base" aria-label="stable">
      →
    </span>
  );
}

export default function TrendingPatternsPanel({ patterns, loading }: TrendingPatternsPanelProps) {
  const sorted = [...patterns].sort((a, b) => b.trend_direction - a.trend_direction);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
          Trending Patterns
        </h3>
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            No trending patterns found.
          </p>
        )}

        {!loading && sorted.length > 0 && (
          <div className="space-y-3">
            {sorted.map((pattern) => (
              <div
                key={pattern.id}
                className={`rounded-lg border p-4 ${
                  pattern.is_trending
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                        {pattern.field_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {pattern.issue_category}
                      </span>
                      {pattern.is_trending && (
                        <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold">
                          Trending
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {pattern.pattern_description}
                    </p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>{pattern.occurrence_count} occurrences</span>
                      <span>{Math.round(pattern.rejection_rate * 100)}% rejection rate</span>
                      <span>{Math.round(pattern.confidence * 100)}% confidence</span>
                    </div>
                  </div>
                  <TrendArrow direction={pattern.trend_direction} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
